/**
 * Pure logic for the Pool / Combine Product operation — the safety rule and
 * the combined volume/LIMS math. Dependency-free so it can be unit-tested;
 * see `productPool.test.ts`.
 *
 * The LIMS arithmetic is NOT here. It moved to the server in z8nrz7e32b —
 * `cgt-backend/src/products/pool-calculations.ts` — because a dose figure must
 * not be computed by a caller and then trusted, and because the pooling rules
 * (three prioritised dose sources, a viability factor, buffer in the
 * denominator) cannot be expressed by the label-matching heuristic that used
 * to stand in for them. What remains here is the picker's safety rule, which
 * exists to keep the UI from offering a selection the server would reject.
 */

export interface PoolCandidate {
  id: string
  patientMrn?: string | null
  /** The product's class, resolved from its catalog entry. */
  productClass: string
}

/**
 * Whether `candidate` may be pooled with everything already selected.
 *
 * The hard safety rule, in its v1 form: same patient and same product class.
 * "Same patient" stands in for the ticket's "same donor" because no donor
 * identifier exists on a product — donor data lives in the Donor Record form.
 * It errs narrow on purpose: it can refuse a pool that would have been valid
 * for an allogeneic donor, but it can never permit combining two patients'
 * material, which is the failure that matters.
 *
 * The server enforces this too. This function exists to keep the picker from
 * offering an option the server would reject, not as the control itself.
 */
export function canPoolWith(selected: PoolCandidate[], candidate: PoolCandidate): boolean {
  if (selected.length === 0) return true
  if (selected.some(s => s.id === candidate.id)) return false
  const [first] = selected
  return (
    (first.patientMrn ?? '') === (candidate.patientMrn ?? '') &&
    first.productClass === candidate.productClass
  )
}

/** Why a candidate is not poolable with the current selection — for the UI to explain itself. */
export function poolRejectionReason(
  selected: PoolCandidate[],
  candidate: PoolCandidate,
): string | null {
  if (canPoolWith(selected, candidate)) return null
  const [first] = selected
  if ((first.patientMrn ?? '') !== (candidate.patientMrn ?? '')) return 'Different patient'
  if (first.productClass !== candidate.productClass) return 'Different product class'
  return 'Already selected'
}

/** Combined volume of the pooled bag: the sum of what went into it. */
export function pooledVolume(volumes: (number | null | undefined)[]): number {
  return Math.round(volumes.reduce<number>((sum, v) => sum + (v ?? 0), 0) * 100) / 100
}

